/** Shared prompt-part types, derived from the generated OpenAPI schema. */

import type {components} from './generated/wb-agent-types';

type Schemas = components['schemas'];
type ArrayElement<T> = T extends ReadonlyArray<infer U> ? U : never;

/**
 * The full BE `user_prompt` payload: either a plain string or an ordered
 * array of discriminated `PromptPart`s. Mirrors HttpUserPrompt in
 * services/wb_agent/src/http/prompt_parts.py.
 */
export type HttpUserPrompt = Schemas['TurnResponse']['user_prompt'];

/**
 * Every prompt part the BE may send, including the `PromptUnknownPart`
 * catch-all (whose `type: string` is wider than the known literals). Use
 * this at boundaries that consume parts from the BE; narrow with
 * `isKnownPromptPart` before switching on `part.type`.
 */
export type AnyPromptPart = ArrayElement<Exclude<HttpUserPrompt, string>>;

/**
 * Filter out PromptUnknownPart: it has `type: string` (any string), while
 * known parts have a literal like `type: 'text'`. `string extends T['type']`
 * is true only for the unknown fallback.
 */
type WithLiteralType<T> = T extends {type: infer U}
  ? string extends U
    ? never
    : T
  : never;

/**
 * Prompt parts whose `type` field is a known literal — safe for exhaustive
 * `switch (part.type)` handling without per-case `as` casts.
 */
export type PromptPart = WithLiteralType<AnyPromptPart>;
export type PromptPartType = PromptPart['type'];

/** Per-variant aliases, extracted from the discriminated union. */
export type PromptTextPart = Extract<PromptPart, {type: 'text'}>;
export type PromptImagePart = Extract<PromptPart, {type: 'image'}>;
export type PromptWandbEntityPart = Extract<PromptPart, {type: 'wandb_entity'}>;
export type PromptWandbProjectPart = Extract<
  PromptPart,
  {type: 'wandb_project'}
>;
export type PromptWandbRunFilePart = Extract<
  PromptPart,
  {type: 'wandb_run_file'}
>;
export type PromptWandbRunPart = Extract<PromptPart, {type: 'wandb_run'}>;
export type PromptWandbCallPart = Extract<PromptPart, {type: 'wandb_call'}>;
export type PromptWandbConversationPart = Extract<
  PromptPart,
  {type: 'wandb_conversation'}
>;
export type PromptWandbArtifactCollectionPart = Extract<
  PromptPart,
  {type: 'wandb_artifact_collection'}
>;
export type PromptWandbWorkspacePanelPart = Extract<
  PromptPart,
  {type: 'wandb_workspace_panel'}
>;
export type PromptWandbWebPageStatePart = Extract<
  PromptPart,
  {type: 'wandb_web_page_state'}
>;
/** Discriminated union of page-content variants on `PromptWandbWebPageStatePart`. */
export type PagePromptContent = ArrayElement<
  NonNullable<PromptWandbWebPageStatePart['content']>
>;

/** Workspace variant of `PagePromptContent`. */
export type WorkspacePageContent = Extract<
  PagePromptContent,
  {type: 'workspace'}
>;

/** The set of image media types the BE accepts on `PromptImagePart`. */
export type ImageMediaType = PromptImagePart['media_type'];

const KNOWN_PROMPT_TYPES: ReadonlySet<string> = new Set<PromptPartType>([
  'text',
  'image',
  'wandb_entity',
  'wandb_project',
  'wandb_run',
  'wandb_run_file',
  'wandb_call',
  'wandb_conversation',
  'wandb_artifact_collection',
  'wandb_workspace_panel',
  'wandb_web_page_state',
]);

/**
 * Discriminates known prompt parts from `PromptUnknownPart` at runtime so
 * a subsequent `switch (part.type)` narrows to the variant interface without
 * per-case `as` casts. Use at every BE-data boundary.
 */
export function isKnownPromptPart(part: AnyPromptPart): part is PromptPart {
  return KNOWN_PROMPT_TYPES.has(part.type);
}
